import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} CartToastRefs
 * @property {HTMLElement} toast
 * @property {HTMLElement} message
 */
class CartToastComponent extends Component {
  requiredRefs = ['toast', 'message'];

  /** @type {number | undefined} */
  #dismissTimeout;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartError);

    if (this.#dismissTimeout) {
      window.clearTimeout(this.#dismissTimeout);
    }
  }

  dismiss = () => {
    if (this.#dismissTimeout) {
      window.clearTimeout(this.#dismissTimeout);
      this.#dismissTimeout = undefined;
    }

    this.refs.toast.dataset.visible = 'false';
    this.refs.toast.hidden = true;
    this.refs.message.textContent = '';
  };

  /**
   * @param {Event} event
   */
  #handleCartError = (event) => {
    const cartErrorEvent = /** @type {import('./events').CartErrorEvent} */ (event);
    const message = this.#normalizeMessage(
      cartErrorEvent.detail?.data?.description ||
        cartErrorEvent.detail?.data?.message ||
        cartErrorEvent.detail?.data?.errors ||
        ''
    );

    if (!message.trim()) {
      return;
    }

    this.refs.message.textContent = message.trim();
    this.refs.toast.hidden = false;
    this.refs.toast.dataset.visible = 'true';

    if (this.#dismissTimeout) {
      window.clearTimeout(this.#dismissTimeout);
    }

    const duration = Number(this.dataset.duration) || 5000;
    this.#dismissTimeout = window.setTimeout(() => this.dismiss(), duration);
  };

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #normalizeMessage(value) {
    if (typeof value === 'string') {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((entry) => this.#normalizeMessage(entry)).filter(Boolean).join(' ');
    }

    if (value && typeof value === 'object') {
      return Object.values(value)
        .map((entry) => this.#normalizeMessage(entry))
        .filter(Boolean)
        .join(' ');
    }

    return '';
  }
}

if (!customElements.get('cart-toast-component')) {
  customElements.define('cart-toast-component', CartToastComponent);
}
